import type { QueryFunctionContext } from '@tanstack/react-query';
import { AsrProxyAdminService, type AsrProxyModelRowDto, FeatureFlagsService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import type { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import type { TAsrResubmitModel } from 'app/queries/asrProxy/types/TAsrResubmitModel';
import type { TAsrResubmitModelOptions } from 'app/queries/asrProxy/types/TAsrResubmitModelOptions';
import type { TCall } from 'app/queries/types/TCall';

type TParams = QueryFunctionContext<ReturnType<typeof asrProxyKeys.resubmitModelOptions>>;

const silentCallOptions = {
  dontRedirectOnError: true,
  onError: () => undefined,
};

const toResubmitModel = (row: AsrProxyModelRowDto): TAsrResubmitModel => ({
  name: row.model_name,
  provider: row.provider,
  providerModel: row.provider_model,
  hasApiKey: row.has_api_key !== false,
  healthy: row.health?.ok !== false,
  healthDetail: row.health?.detail ?? '',
  supportsDiarization: row.info?.supports_diarization === true,
  supportedOptions: Array.isArray(row.supported_options) ? row.supported_options : null,
});

export const createFetchAsrResubmitModelOptionsQueryFn =
  (call: TCall) =>
  async ({ signal }: TParams): Promise<TAsrResubmitModelOptions> => {
    const flags = await call(() => cancelOnSignal(FeatureFlagsService.findAll(), signal), silentCallOptions);
    const asrProxyDisabled =
      Array.isArray(flags) && flags.some((flag) => flag.name === 'ASR_PROXY_DISABLED' && flag.value === true);
    if (asrProxyDisabled) {
      return { asrProxyDisabled: true, defaultModel: '', models: [] };
    }
    const response = await call(() => cancelOnSignal(AsrProxyAdminService.models(), signal), silentCallOptions);

    return {
      asrProxyDisabled: false,
      defaultModel: response?.default_model ?? '',
      models: Array.isArray(response?.models) ? response.models.map(toResubmitModel) : [],
    };
  };
